/**
 * services/TradingObjectiveService.ts
 * Service for managing trading objectives (session goals)
 *
 * Extends BaseService to provide CRUD operations for user's trading objectives.
 * Objectives track behavioral and outcome goals across trading sessions.
 */

import { BaseService } from './BaseService';
import type { TradingObjective } from '@/types/schema/TradingObjective';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import { isEntityActive } from '@/types/shared/EntityStatus';

/**
 * Service for managing trading objectives
 *
 * Provides CRUD operations plus status-based filtering methods.
 * Inherits getAll(), add(), update(), softDelete() from BaseService.
 */
export class TradingObjectiveService extends BaseService<TradingObjective> {
  constructor() {
    super('trading_objectives');
  }

  /**
   * Get all active trading objectives
   *
   * Filters objectives where status is 'active'.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @returns Promise resolving to array of active objectives
   */
  async getActive(): Promise<TradingObjective[]> {
    const allObjectives = await this.getAll();
    return allObjectives.filter(objective => isEntityActive(objective.status));
  }

  /**
   * Get trading objectives by status
   *
   * Filters objectives by the specified EntityStatus.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param status - The status to filter by ('active' | 'inactive' | 'archived')
   * @returns Promise resolving to array of objectives with matching status
   */
  async getByStatus(status: EntityStatus): Promise<TradingObjective[]> {
    const allObjectives = await this.getAll();
    return allObjectives.filter(objective => objective.status === status);
  }
}

// Export singleton instance
export const tradingObjectiveService = new TradingObjectiveService();
